import { Tab, Tabs } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useGetTimeEntries } from "./useGetTimeEntries";
import { useGetTimeEntriesGroupedByUser } from "./useGetTimeEntriesGroupedByUser";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import { sortDirectionMap } from "constants/sortDirectionMap";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import {
  timeEntriesInitialSortDirectionKey,
  timeEntriesInitialSortKeyKey,
  timeEntriesInitialSortKeyValue,
  timeEntriesSortKeyMap,
} from "constants/timeEntries/sortKeys";
import TimeEntryActionsCell from "modules/TimeEntries/TimeEntryActionsCell";
import { JobPhaseAutocomplete } from "components/JobPhaseScrollAsyncAutocomplete/JobPhaseAutocomplete";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import MDTypography from "components/MDTypography";
import OrganizationUsersOptions from "modules/organization/OrganizationUsersOptions/OrganizationUsersOptions";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

dayjs.extend(duration);

type GroupMode = "date" | "user";

export const useTimeEntriesData = () => {
  const [groupMode, setGroupMode] = useState<GroupMode>("date");
  const [collapsedDates, setCollapsedDates] = useState<Record<string, boolean>>({});
  const [collapsedUsers, setCollapsedUsers] = useState<Record<string, boolean>>({});

  const { filtering, offset, sorting } = usePaginatedVariables({
    initialSortDirection: timeEntriesInitialSortDirectionKey,
    initialSortKey: timeEntriesInitialSortKeyKey,
    initialJobPhaseId: null,
    initialUserID: null,
    initialForDate: null,
    initialForDateStart: null,
    initialForDateEnd: null,
  });

  const stripTime = (date: string) => {
    return dayjs(date).format("YYYY-MM-DD");
  };

  const {
    data: dateEntriesData,
    loading: dateEntriesLoading,
    error: dateEntriesError,
    paginatorInfo: dateEntriesPaginatorInfo,
  } = useGetTimeEntries({
    skip: groupMode !== "date",
    variables: {
      page: offset.page,
      first: offset.first,
      sortKey: timeEntriesSortKeyMap[sorting.sortKey] || timeEntriesInitialSortKeyValue,
      sortDirection: sortDirectionMap[sorting.sortDirection],
      jobPhaseId: filtering.jobPhaseId,
      userId: filtering.userID,
      forDate: filtering.forDate ? stripTime(filtering.forDate) : null,
      forDateStart: filtering.forDateStart ? stripTime(filtering.forDateStart) : null,
      forDateEnd: filtering.forDateEnd ? stripTime(filtering.forDateEnd) : null,
    },
  });

  const {
    data: groupedUserData,
    loading: groupedUserLoading,
    error: groupedUserError,
    paginatorInfo: groupedUserPaginatorInfo,
  } = useGetTimeEntriesGroupedByUser({
    skip: groupMode !== "user",
    variables: {
      page: offset.page,
      first: offset.first,
      sortDirection: sortDirectionMap[sorting.sortDirection],
      jobPhaseId: filtering.jobPhaseId,
      userId: filtering.userID,
      forDate: filtering.forDate ? stripTime(filtering.forDate) : null,
      forDateStart: filtering.forDateStart ? stripTime(filtering.forDateStart) : null,
      forDateEnd: filtering.forDateEnd ? stripTime(filtering.forDateEnd) : null,
    },
  });

  const loading = groupMode === "date" ? dateEntriesLoading : groupedUserLoading;
  const error = groupMode === "date" ? dateEntriesError : groupedUserError;
  const paginatorInfo = groupMode === "date" ? dateEntriesPaginatorInfo : groupedUserPaginatorInfo;

  const pagination = usePaginatedResources({
    paginate: offset.paginate,
    paginatorInfo,
  });

  const toggleDateGroup = useCallback((date: string) => {
    setCollapsedDates((previousState) => ({
      ...previousState,
      [date]: !previousState[date],
    }));
  }, []);

  const toggleUserGroup = useCallback((userId: string) => {
    setCollapsedUsers((previousState) => ({
      ...previousState,
      [userId]: !previousState[userId],
    }));
  }, []);

  const ViewModeSwitch = useCallback(() => {
    return (
      <Tabs
        value={groupMode}
        onChange={(_, value) => setGroupMode(value)}
        sx={{ minHeight: "34px", "& .MuiTab-root": { minHeight: "34px", px: 1, py: 0.25, fontSize: "0.75rem" } }}
      >
        <Tab label="Date" value="date" />
        <Tab label="User" value="user" />
      </Tabs>
    );
  }, [groupMode]);

  const JobPhaseFilter = useCallback(() => {
    return (
      <JobPhaseAutocomplete>
        {({ options, pagination, handleSearch }) => (
          <>
            <MDBox
              display="flex"
              width="100%"
              alignItems="center"
              sx={{
                "& > div:first-of-type": { width: filtering.jobPhaseId ? "96%" : "100%" },
                "& .MuiInputBase-root": { minHeight: "38px" },
                "& .MuiFormLabel-root": { fontSize: "0.8rem" },
              }}
            >
              <ScrollAsyncAutocomplete
                label="Filter by Job Phase"
                value={options.find((x) => x.value === filtering.jobPhaseId)}
                onChange={(value) => filtering.setJobPhaseId(value as string)}
                options={options}
                loading={false}
                hasMore={pagination.hasNextPage}
                search={handleSearch}
                onLoadMore={pagination.loadMore}
              />
              {filtering.jobPhaseId && (
                <MDBox ml={1}>
                  <MDTypography
                    component="i"
                    variant="body2"
                    color="secondary"
                    onClick={() => filtering.setJobPhaseId(null)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        color: "error.main",
                      },
                    }}
                  >
                    &#x2715;
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </>
        )}
      </JobPhaseAutocomplete>
    );
  }, [filtering]);

  const UserFilter = useCallback(() => {
    return (
      <OrganizationUsersOptions queryOverride={{ first: 1000 }}>
        {({ organizationUsersOptions, loading }) => (
          <MDBox
            sx={{
              "& .MuiInputBase-root": { minHeight: "38px" },
              "& .MuiFormLabel-root": { fontSize: "0.8rem" },
            }}
          >
            <AutoComplete
              options={organizationUsersOptions}
              loading={loading}
              variant="outlined"
              field={{
                onChange: (value) => filtering.setUserID(value as string),
                onBlur: () => {},
                value: filtering.userID,
                name: "userID",
                ref: null,
              }}
              error={null}
              placeholder="Select User"
            />
          </MDBox>
        )}
      </OrganizationUsersOptions>
    );
  }, [filtering.setUserID, filtering.userID]);

  const DateFilter = useCallback(() => {
    const handleDateChange = (date: Date | null, type: "start" | "end") => {
      const formattedDate = date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : null;
      filtering.setForDate(null);

      if (type === "start") {
        filtering.setForDateStart(formattedDate);
        return;
      }

      filtering.setForDateEnd(formattedDate);
    };

    return (
      <MDBox width="100%" display="flex" gap={1}>
        <MDBox display="flex" gap={1} width="100%">
          <DatePicker
            selected={filtering.forDateStart ? new Date(filtering.forDateStart) : null}
            onChange={(date) => handleDateChange(date, "start")}
            dateFormat="yyyy-MM-dd"
            isClearable
            placeholderText="Start Date"
            customInput={
              <MDInput
                variant="outlined"
                label="Start Date"
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root, & .MuiFormControl-root, & .MuiInputBase-input, & .MuiTextField-root":
                    {
                      height: "44px",
                      minHeight: "38px",
                      maxWidth: "100%",
                      width: "100%",
                    },
                }}
              />
            }
          />
          <DatePicker
            selected={filtering.forDateEnd ? new Date(filtering.forDateEnd) : null}
            onChange={(date) => handleDateChange(date, "end")}
            dateFormat="yyyy-MM-dd"
            isClearable
            placeholderText="End Date"
            customInput={
              <MDInput
                variant="outlined"
                label="End Date"
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root, & .MuiFormControl-root, & .MuiInputBase-input, & .MuiTextField-root":
                    {
                      height: "44px",
                      minHeight: "38px",
                      maxWidth: "100%",
                      width: "100%",
                    },
                }}
              />
            }
          />
        </MDBox>
      </MDBox>
    );
  }, [filtering]);

  const columns = useMemo(() => {
    return [
      {
        Header: "Date",
        accessor: "forDate",
      },
      {
        Header: "User",
        accessor: "user.name",
      },
      {
        Header: "Job ID",
        accessor: "jobPhase.externalId",
        Cell: ({ value, row }) => {
          const jobId = row?.original?.jobPhase?.job?.id;
          if (!jobId) {
            return <MDTypography variant="button">{value || "—"}</MDTypography>;
          }

          return (
            <MDTypography
              component="a"
              href={`/jobs/${jobId}`}
              color="info"
              sx={{
                cursor: "pointer",
              }}
              variant="button"
            >
              {value}
            </MDTypography>
          );
        },
      },
      {
        Header: "Job Name",
        accessor: "jobPhase.proposalStage.name",
        disableSortBy: true,
      },
      {
        Header: "Clock In Time",
        accessor: "startTime",
        Cell: ({ value }) => (value ? dayjs(value).format("hh:mm A") : "—"),
      },
      {
        Header: "Clock Out Time",
        accessor: "endTime",
        Cell: ({ value }) => (value ? dayjs(value).format("hh:mm A") : "—"),
      },
      {
        Header: "Total Hours",
        accessor: "timeTrackedSeconds",
        Cell: ({ value }) => {
          return dayjs.duration(value, "seconds").format("H:mm");
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "right",
        disableSortBy: true,
        Cell: TimeEntryActionsCell,
      },
    ];
  }, []);

  const groupedRowsByDate = useMemo(() => {
    const sourceRows = dateEntriesData ?? [];
    const dateEntryCountMap = sourceRows.reduce<Record<string, number>>((accumulator, row) => {
      const dateKey = row.forDate ? dayjs(row.forDate).format("YYYY-MM-DD") : "unknown";
      accumulator[dateKey] = (accumulator[dateKey] ?? 0) + 1;
      return accumulator;
    }, {});
    const dateTotalSecondsMap = sourceRows.reduce<Record<string, number>>((accumulator, row) => {
      const dateKey = row.forDate ? dayjs(row.forDate).format("YYYY-MM-DD") : "unknown";
      accumulator[dateKey] = (accumulator[dateKey] ?? 0) + (row.timeTrackedSeconds ?? 0);
      return accumulator;
    }, {});

    return sourceRows.reduce<any[]>((accumulator, row) => {
      const dateKey = row.forDate ? dayjs(row.forDate).format("YYYY-MM-DD") : "unknown";
      const hasGroupHeader = accumulator.some((tableRow) => tableRow.groupDateKey === dateKey);

      if (!hasGroupHeader) {
        accumulator.push({
          id: `group-date-${dateKey}`,
          isGroupHeader: true,
          groupType: "date",
          groupDateKey: dateKey,
          groupForDate: row.forDate,
          groupCount: dateEntryCountMap[dateKey] ?? 0,
          groupTotalTimeTrackedSeconds: dateTotalSecondsMap[dateKey] ?? 0,
          groupCollapsed: Boolean(collapsedDates[dateKey]),
          forDate: row.forDate,
          startTime: null,
          endTime: null,
          timeTrackedSeconds: null,
          user: { name: "" },
          jobPhase: { externalId: "" },
        });
      }

      if (!collapsedDates[dateKey]) {
        accumulator.push(row);
      }

      return accumulator;
    }, []);
  }, [collapsedDates, dateEntriesData]);

  const groupedRowsByUser = useMemo(() => {
    const sourceGroups = groupedUserData ?? [];

    return sourceGroups.reduce((rows: any[], userGroup: any) => {
      const userId = userGroup?.user?.id;
      if (!userId) {
        return rows;
      }

      const userCollapsed = Boolean(collapsedUsers[userId]);

      rows.push({
        id: `group-user-${userId}`,
        isGroupHeader: true,
        groupType: "user",
        groupUserId: userId,
        groupUserName: userGroup?.user?.name || "Unknown User",
        groupCount: userGroup?.totalEntries ?? 0,
        groupTotalTimeTrackedSeconds: userGroup?.totalTimeTrackedSeconds ?? 0,
        groupCollapsed: userCollapsed,
      });

      if (userCollapsed) {
        return rows;
      }

      const dateGroups = userGroup?.dates ?? [];
      dateGroups.forEach((dateGroup: any) => {
        (dateGroup?.entries ?? []).forEach((entry) => {
          rows.push(entry);
        });
      });

      return rows;
    }, []);
  }, [collapsedUsers, groupedUserData]);

  const rows = groupMode === "date" ? groupedRowsByDate : groupedRowsByUser;

  const tableData = useMemo(() => {
    return { columns, rows };
  }, [columns, rows]);

  return {
    data: groupMode === "date" ? dateEntriesData : groupedUserData,
    loading,
    error,
    columns,
    tableData,
    pagination,
    filtering,
    paginatorInfo,
    sorting,
    groupMode,
    setGroupMode,
    JobPhaseFilter,
    UserFilter,
    DateFilter,
    ViewModeSwitch,
    toggleDateGroup,
    toggleUserGroup,
  } as const;
};
